import { useEffect, useState } from "react";

import { api, ApiError } from "../api/client";
import { formatDateTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { ui } from "../lib/ui";
import type { GpuInfo } from "../types";
import { Panel } from "./Panel";
import { ProgressBar } from "./ProgressBar";

const REFRESH_MS = 4000;

/** How many queue workers this PC runs, what each is doing (by PID), and
 *  the GPU they share as nvidia-smi sees it. */
export function WorkersPanel() {
  const info = useAsync(() => api.workers(), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") info.reload();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [info.reload]);

  const setCount = async (count: number | null) => {
    setBusy(true);
    setError(null);
    try {
      await api.setWorkers(count);
      info.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const data = info.data;
  const others = data?.gpu_processes.filter((p) => !p.is_worker) ?? [];

  return (
    <Panel
      title="Workers"
      subtitle="Each worker processes one queued day at a time. More than one only helps when the GPU has room to spare — watch its memory and load below."
      actions={
        data && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted">
              {data.from_console ? "set here" : "install default"}
            </span>
            <div className="inline-flex items-center overflow-hidden rounded-lg border border-border">
              <button
                type="button"
                className="px-3 py-1.5 enabled:hover:bg-bg disabled:opacity-40"
                disabled={busy || data.count <= 0}
                onClick={() => setCount(data.count - 1)}
                aria-label="Fewer workers"
              >
                −
              </button>
              <span className="min-w-8 border-x border-border px-2 py-1.5 text-center font-semibold tabular-nums">
                {data.count}
              </span>
              <button
                type="button"
                className="px-3 py-1.5 enabled:hover:bg-bg disabled:opacity-40"
                disabled={busy || data.count >= data.max_count}
                onClick={() => setCount(data.count + 1)}
                aria-label="More workers"
              >
                +
              </button>
            </div>
            {data.from_console && (
              <button
                type="button"
                className={ui.btnQuiet}
                disabled={busy}
                onClick={() => setCount(null)}
                title={`Back to what the installer set: ${data.install_default}`}
              >
                Reset
              </button>
            )}
          </div>
        )
      }
    >
      {(error || info.error) && (
        <p className={`${ui.notice} ${ui.noticeError}`}>{error ?? info.error}</p>
      )}
      {!data && !info.error && <p className="text-muted">Loading…</p>}
      {data && !data.supervised && (
        <p className={ui.notice}>
          Workers on this machine are started outside the app (e.g.{" "}
          <span className="font-mono text-xs">make worker</span>), so the count above
          only applies to the desktop app.
        </p>
      )}

      {data && (
        <div className="flex flex-col gap-4">
          {data.gpus && data.gpus.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {data.gpus.map((gpu) => (
                <GpuCard key={gpu.index} gpu={gpu} />
              ))}
            </div>
          )}
          {data.gpus === null && (
            <p className="text-xs text-muted">
              No NVIDIA GPU (or no driver) on this PC — workers run on the CPU or an
              Intel GPU through OpenVINO.
            </p>
          )}

          {data.workers.length === 0 ? (
            <p className="text-muted">
              {data.count === 0
                ? "No workers: queued runs wait until you add one."
                : "Starting workers…"}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>PID</th>
                    <th>Device</th>
                    <th>Doing</th>
                    <th title="As nvidia-smi reports it; Windows usually cannot say per process">
                      GPU memory
                    </th>
                    <th>Since</th>
                  </tr>
                </thead>
                <tbody>
                  {data.workers.map((worker) => (
                    <tr key={worker.id}>
                      <td>{worker.slot ?? "—"}</td>
                      <td className="font-mono text-xs">{worker.pid}</td>
                      <td className="text-muted">{worker.device ?? "—"}</td>
                      <td className="min-w-44">
                        {worker.run_id === null ? (
                          <span className="text-muted">idle</span>
                        ) : (
                          <>
                            <span>
                              Run #{worker.run_id}
                              {worker.run_date && (
                                <span className="text-muted">
                                  {" "}
                                  · {worker.run_branch_id} {worker.run_date}
                                </span>
                              )}
                            </span>
                            <ProgressBar className="mt-1 w-44" value={worker.run_progress} />
                          </>
                        )}
                      </td>
                      <td>
                        {worker.on_gpu ? (
                          <span className={`${ui.pill} ${ui.pillSucceeded}`}>
                            CUDA{worker.gpu_memory_mb !== null && ` · ${worker.gpu_memory_mb} MB`}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap text-muted">
                        {formatDateTime(worker.started_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {others.length > 0 && (
            <div className="text-xs text-muted">
              <span className={ui.label}>Other CUDA processes</span>
              <ul className="mt-1 flex flex-col gap-0.5">
                {others.map((p) => (
                  <li key={p.pid} className="font-mono">
                    {p.pid} {p.name}
                    {p.used_memory_mb !== null && ` — ${p.used_memory_mb} MB`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function GpuCard({ gpu }: { gpu: GpuInfo }) {
  const memory =
    gpu.memory_used_mb !== null && gpu.memory_total_mb
      ? gpu.memory_used_mb / gpu.memory_total_mb
      : null;
  return (
    <div className="flex flex-col gap-2 rounded-card border border-border bg-bg/60 px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold">{gpu.name}</span>
        <span className="text-xs text-muted">
          {[
            gpu.temperature_c !== null && `${gpu.temperature_c} °C`,
            gpu.power_w !== null && `${Math.round(gpu.power_w)} W`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
      <div>
        <div className="flex justify-between text-xs text-muted">
          <span>Load</span>
          <span>{gpu.utilization !== null ? `${gpu.utilization}%` : "—"}</span>
        </div>
        <ProgressBar value={gpu.utilization !== null ? gpu.utilization / 100 : 0} />
      </div>
      <div>
        <div className="flex justify-between text-xs text-muted">
          <span>Memory</span>
          <span>
            {gpu.memory_used_mb !== null && gpu.memory_total_mb !== null
              ? `${(gpu.memory_used_mb / 1024).toFixed(1)} / ${(gpu.memory_total_mb / 1024).toFixed(1)} GB`
              : "—"}
          </span>
        </div>
        <ProgressBar
          value={memory ?? 0}
          tone={memory !== null && memory > 0.9 ? "danger" : "accent"}
        />
      </div>
    </div>
  );
}
